import { useState } from 'react'
import { FileText, Mail, MessageSquare, ChevronDown } from 'lucide-react'
import type { DecisionWorkProduct } from '@/lib/api'

// ─── Work-product extraction ───────────────────────────────────────────────────
//
// A decision's `detail` is an opaque bag the agent fills in. Mark's #1 complaint
// was that the queue showed none of the actual work the agent produced — the
// drafted email, the message that would be sent, the numbers behind a financial
// call. This module turns that bag into a readable preview:
//   • a "draft" block (email / message / article / free text), and
//   • a "facts" grid of the scalar fields (amounts, ids, counts, names).
//
// HTML is never injected — email bodies are stripped to plain text for preview,
// so there is no XSS surface even though the content originates from an agent.

type DraftKind = 'email' | 'message' | 'article' | 'text' | 'action'

interface Draft {
  kind: DraftKind
  heading: string | null
  body: string
}

interface Fact {
  label: string
  value: string
}

const EMAIL_SUBJECT = ['emailSubject', 'subject']
const EMAIL_BODY = ['emailHtml', 'emailBody', 'bodyHtml', 'html']
const ARTICLE_TITLE = ['articleTitle', 'title', 'headline']
const ARTICLE_BODY = ['articleBody', 'bodyMarkdown', 'markdown', 'content', 'draft', 'body']
const MESSAGE_BODY = ['message', 'messageText', 'smsBody', 'whatsappBody', 'messageBody']
const TEXT_FALLBACK = ['description', 'recommendation', 'reason', 'text', 'rationale']

// Keys consumed by the draft block or internal — never repeated in the facts grid.
const CONSUMED = new Set([
  ...EMAIL_SUBJECT,
  ...EMAIL_BODY,
  ...ARTICLE_TITLE,
  ...ARTICLE_BODY,
  ...MESSAGE_BODY,
  ...TEXT_FALLBACK,
  '_dedupKey',
])

function firstString(obj: Record<string, unknown>, keys: string[]): { key: string; value: string } | null {
  for (const k of keys) {
    const v = obj[k]
    if (typeof v === 'string' && v.trim()) return { key: k, value: v.trim() }
  }
  return null
}

/** Strip HTML tags + decode the handful of entities email templates use. */
function stripHtml(html: string): string {
  return html
    .replace(/<\s*br\s*\/?>/gi, '\n')
    .replace(/<\/\s*p\s*>/gi, '\n\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&#39;|&apos;/g, "'")
    .replace(/&quot;/g, '"')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}

function humanizeKey(key: string): string {
  return key
    .replace(/_/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim()
}

function formatValue(v: unknown): string | null {
  if (typeof v === 'string') {
    const t = v.trim()
    if (!t || t.length > 80) return null // long strings belong in the draft block, not the grid
    return t
  }
  if (typeof v === 'number') return Number.isInteger(v) ? v.toLocaleString() : String(v)
  if (typeof v === 'boolean') return v ? 'Yes' : 'No'
  return null
}

export interface ParsedWorkProduct {
  draft: Draft | null
  facts: Fact[]
}

/** Parse a decision's `detail` into a draft + facts. Exported for unit testing. */
export function parseWorkProduct(detail: unknown): ParsedWorkProduct {
  if (typeof detail === 'string') {
    const t = detail.trim()
    return { draft: t ? { kind: 'text', heading: null, body: t } : null, facts: [] }
  }
  if (!detail || typeof detail !== 'object') return { draft: null, facts: [] }

  const obj = detail as Record<string, unknown>
  let draft: Draft | null = null

  const subject = firstString(obj, EMAIL_SUBJECT)
  const emailBody = firstString(obj, EMAIL_BODY)
  const articleBody = firstString(obj, ARTICLE_BODY)
  const articleTitle = firstString(obj, ARTICLE_TITLE)
  const message = firstString(obj, MESSAGE_BODY)
  const fallback = firstString(obj, TEXT_FALLBACK)

  if (emailBody || (subject && !articleBody)) {
    draft = {
      kind: 'email',
      heading: subject?.value ?? null,
      body: emailBody ? stripHtml(emailBody.value) : '',
    }
  } else if (articleBody) {
    draft = { kind: 'article', heading: articleTitle?.value ?? null, body: articleBody.value.trim() }
  } else if (message) {
    draft = { kind: 'message', heading: null, body: message.value }
  } else if (fallback) {
    draft = { kind: 'text', heading: null, body: fallback.value }
  }

  const facts: Fact[] = []
  for (const [key, raw] of Object.entries(obj)) {
    if (CONSUMED.has(key) || key.startsWith('_')) continue
    const value = formatValue(raw)
    if (value !== null) facts.push({ label: humanizeKey(key), value })
  }

  return { draft, facts }
}

const KIND_META: Record<DraftKind, { icon: typeof FileText; label: string }> = {
  email: { icon: Mail, label: 'Drafted email' },
  message: { icon: MessageSquare, label: 'Message to send' },
  article: { icon: FileText, label: 'Drafted content' },
  text: { icon: FileText, label: 'Details' },
  action: { icon: FileText, label: 'Proposed action' },
}

const PREVIEW_CHARS = 280

/**
 * Render the work the agent produced. Prefers an explicit, agent-authored
 * `workProduct` (deterministic) and only falls back to heuristically parsing
 * the `detail` bag when one wasn't supplied. The facts grid always comes from
 * `detail` so scalar context (ids, amounts, counts) still shows either way.
 */
export function WorkProduct({
  detail,
  workProduct,
}: {
  detail: unknown
  workProduct?: DecisionWorkProduct | null
}) {
  const parsed = parseWorkProduct(detail)
  const draft: Draft | null = workProduct
    ? { kind: workProduct.kind, heading: workProduct.heading ?? null, body: workProduct.body }
    : parsed.draft
  const facts = parsed.facts
  const [expanded, setExpanded] = useState(false)

  if (!draft && facts.length === 0) return null

  const isLong = draft ? draft.body.length > PREVIEW_CHARS : false
  const shown = draft && isLong && !expanded ? draft.body.slice(0, PREVIEW_CHARS).trimEnd() + '…' : draft?.body

  return (
    <div className="mt-3 ml-3 space-y-3">
      {draft && (draft.body || draft.heading) && (
        <div className="bg-cmd-base/60 border-l-2 border-cmd-outline-variant">
          <DraftHeader kind={draft.kind} heading={draft.heading} />
          {draft.body && (
            <p className="whitespace-pre-wrap px-3 pb-3 text-[0.8rem] leading-relaxed text-white/80">
              {shown}
            </p>
          )}
          {isLong && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                setExpanded((v) => !v)
              }}
              className="flex items-center gap-1 px-3 pb-2.5 font-label text-[0.65rem] tracking-wider text-primary hover:underline focus-visible:outline-none"
            >
              <ChevronDown className={`h-3 w-3 transition-transform ${expanded ? 'rotate-180' : ''}`} aria-hidden="true" />
              {expanded ? 'Show less' : 'View full detail'}
            </button>
          )}
        </div>
      )}

      {facts.length > 0 && (
        <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 sm:grid-cols-3">
          {facts.map((f) => (
            <div key={f.label}>
              <dt className="font-label text-[0.55rem] tracking-widest text-cmd-outline uppercase">{f.label}</dt>
              <dd className="mt-0.5 font-mono text-xs text-white/90 tabular-nums break-words">{f.value}</dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  )
}

function DraftHeader({ kind, heading }: { kind: DraftKind; heading: string | null }) {
  const meta = KIND_META[kind]
  const Icon = meta.icon
  return (
    <div className="flex items-start gap-1.5 px-3 pt-2.5 pb-1.5">
      <Icon className="mt-0.5 h-3 w-3 shrink-0 text-cmd-outline" aria-hidden="true" />
      <div className="min-w-0">
        <p className="font-label text-[0.55rem] tracking-widest text-cmd-outline uppercase">{meta.label}</p>
        {heading && <p className="mt-0.5 text-[0.8rem] font-medium text-white/90 break-words">{heading}</p>}
      </div>
    </div>
  )
}
